import type {
  Area,
  AnyEntity,
  CalendarDate,
  EditorDocument,
  EntityKind,
  Folder,
  Instant,
  Mutation,
  MutationEnvelope,
  Note,
  Priority,
  Project,
  Reminder,
  ReminderTarget,
  Task,
  TaskStatus,
} from "@hq/contracts";
import { mutationEntityKind, mutationTargetId } from "@hq/contracts";
import {
  applyMutation,
  createFolder,
  createNote,
  extractNote,
  createProject,
  createArea,
  createReminder,
  createTask,
  dailyNoteTitle,
  newId,
  type NewNoteInput,
  type NewTaskInput,
  type NowContext,
  nowContext,
  parseQuickAdd,
  wouldCreateCycle,
} from "@hq/core";
import type { EntityRepository, LocalStore } from "@hq/local-store";
import { rebaseDraftRevision } from "./draftRevision";

/** What the editor saves: its document, and the Markdown mirror written from it. */
export type EditorContent = { document: EditorDocument; markdown: string };

/**
 * A draft the app's autosave carries: editor content, or plain Markdown from a writer that has no
 * document (which then drops the one the note had).
 */
export type ContentDraft = string | EditorContent;

export type ServicesOptions = {
  /** Deterministic time source for tests. Defaults to the wall clock in the local zone. */
  clock?: () => NowContext;
  /** Called after every committed write. The sync engine passes its `kick`. */
  onMutation?: () => void;
  /**
   * Mutation ids currently being pushed. Shared with the sync engine so `notes.updateMarkdown`
   * never rewrites a mutation that is already on the wire.
   */
  inFlight?: Set<string>;
};

export type QuickAddOptions = {
  /** Project to use when the text names none (or names one that does not resolve). */
  projectId?: string;
  sourceNoteId?: string;
  triaged?: boolean;
  plannedDate?: CalendarDate;
  /** Projects to resolve `#name` against. Loaded from the store when omitted. */
  projects?: readonly Project[];
};

export interface TaskServices {
  create(input: NewTaskInput): Promise<Task>;
  createFromQuickAdd(text: string, opts?: QuickAddOptions): Promise<Task>;
  rename(id: string, title: string): Promise<Task>;
  setTags(id: string, tags: readonly string[]): Promise<Task>;
  /** Plain Markdown. Drops the editor document. */
  setDescription(id: string, description: string | null, baseRevision?: number): Promise<Task>;
  /** The editor's document with its Markdown mirror. `null`, or empty Markdown, clears both. */
  setDescriptionContent(
    id: string,
    content: EditorContent | null,
    baseRevision?: number,
  ): Promise<Task>;
  complete(id: string): Promise<Task>;
  reopen(id: string): Promise<Task>;
  toggleComplete(id: string): Promise<Task>;
  setStatus(id: string, status: TaskStatus): Promise<Task>;
  setDueDate(id: string, dueDate: CalendarDate | null): Promise<Task>;
  plan(id: string, plannedDate: CalendarDate | null): Promise<Task>;
  schedule(
    id: string,
    scheduledAt: Instant | null,
    scheduledDurationMinutes?: number | null,
  ): Promise<Task>;
  moveToProject(id: string, projectId: string | null): Promise<Task>;
  setPriority(id: string, priority: Priority | null): Promise<Task>;
  setEvening(id: string, evening: boolean): Promise<Task>;
  setHeading(id: string, heading: string | null): Promise<Task>;
  setEstimate(id: string, estimateMinutes: number | null): Promise<Task>;
  setParent(id: string, parentTaskId: string | null): Promise<Task>;
  reorder(id: string, sortOrder: number): Promise<Task>;
  delete(id: string): Promise<Task>;
  restore(id: string): Promise<Task>;
}

export interface NoteServices {
  create(input?: NewNoteInput): Promise<Note>;
  /**
   * Atomically create the child note for writing moved out of a note, and save the source as the
   * editor left it: with the writing replaced by `noteLink(child)`. The child's id is chosen by the
   * caller, which needed it for the link.
   */
  extract(
    id: string,
    input: {
      child: { id: string; title: string; markdown: string };
      source: EditorContent;
      baseRevision?: number;
    },
  ): Promise<Note>;
  /** Plain Markdown, dropping the editor document. Coalesced like `updateContent`. */
  updateMarkdown(id: string, markdown: string, baseRevision?: number): Promise<Note>;
  /** Revision-guarded and coalesced with the newest pending, not-in-flight write of its kind. */
  updateContent(id: string, content: EditorContent, baseRevision?: number): Promise<Note>;
  rename(id: string, title: string, baseRevision?: number): Promise<Note>;
  move(id: string, folderId: string | null): Promise<Note>;
  reorder(id: string, sortOrder: number): Promise<Note>;
  setPinned(id: string, pinned: boolean): Promise<Note>;
  setTags(id: string, tags: readonly string[]): Promise<Note>;
  delete(id: string): Promise<Note>;
  restore(id: string): Promise<Note>;
  /** The daily note titled `YYYY-MM-DD`, created on first open. */
  openDaily(date: CalendarDate, folderId?: string): Promise<Note>;
}

export interface FolderServices {
  create(input: { name: string; parentId?: string; sortOrder?: number }): Promise<Folder>;
  rename(id: string, name: string): Promise<Folder>;
  /** Throws before writing anything when the move would create a cycle. */
  move(id: string, parentId: string | null): Promise<Folder>;
  reorder(id: string, sortOrder: number): Promise<Folder>;
  delete(id: string): Promise<Folder>;
  restore(id: string): Promise<Folder>;
}

export interface ProjectServices {
  create(input: {
    name: string;
    area?: string;
    notesFolderId?: string;
    sortOrder?: number;
  }): Promise<Project>;
  rename(id: string, name: string): Promise<Project>;
  setArea(id: string, area: string | null): Promise<Project>;
  archive(id: string): Promise<Project>;
  unarchive(id: string): Promise<Project>;
  setNotesFolder(id: string, notesFolderId: string | null): Promise<Project>;
  reorder(id: string, sortOrder: number): Promise<Project>;
  delete(id: string): Promise<Project>;
  restore(id: string): Promise<Project>;
}

export interface ReminderServices {
  create(input: { target: ReminderTarget; title: string; triggerAt: Instant }): Promise<Reminder>;
  reschedule(id: string, triggerAt: Instant): Promise<Reminder>;
  snooze(id: string, snoozedUntil: Instant): Promise<Reminder>;
  dismiss(id: string): Promise<Reminder>;
  markDelivered(id: string): Promise<Reminder>;
  delete(id: string): Promise<Reminder>;
}

export interface AreaServices {
  create(input: { name: string }): Promise<Area>;
}

export interface Services {
  readonly areas: AreaServices;
  readonly tasks: TaskServices;
  readonly notes: NoteServices;
  readonly folders: FolderServices;
  readonly projects: ProjectServices;
  readonly reminders: ReminderServices;
  /** Mutation ids currently being pushed (shared with the sync engine). */
  readonly inFlight: Set<string>;
}

function repoOf(store: LocalStore, kind: EntityKind): EntityRepository<AnyEntity> {
  switch (kind) {
    case "task":
      return store.tasks as unknown as EntityRepository<AnyEntity>;
    case "note":
      return store.notes as unknown as EntityRepository<AnyEntity>;
    case "folder":
      return store.folders as unknown as EntityRepository<AnyEntity>;
    case "area":
      return store.areas as unknown as EntityRepository<AnyEntity>;
    case "project":
      return store.projects as unknown as EntityRepository<AnyEntity>;
    case "reminder":
      return store.reminders as unknown as EntityRepository<AnyEntity>;
  }
}

/**
 * Local application services. Every method is the same shape: build a narrow `Mutation`,
 * then in one transaction apply it to the current entity with `applyMutation`, persist the
 * result, and enqueue the envelope for sync. The promise resolves only after the local store
 * has committed, which is what "Saved locally" means.
 */
export function createServices(store: LocalStore, options: ServicesOptions = {}): Services {
  const clock = options.clock ?? (() => nowContext());
  const inFlight = options.inFlight ?? new Set<string>();

  /**
   * Persist one mutation and its optimistic result atomically. `build` may be a function so
   * revision-guarded writes read `baseRevision` inside the same transaction that persists them;
   * reading it outside could race an own-echo pull landing between the read and the write.
   */
  async function commit<T extends AnyEntity>(
    build:
      | Mutation
      | ((current: AnyEntity | undefined, tx: LocalStore) => Mutation | Promise<Mutation>),
    target?: { kind: EntityKind; id: string },
    prepare?: (tx: LocalStore, current: AnyEntity | undefined) => Promise<void>,
  ): Promise<T> {
    const ctx = clock();
    const kind = target?.kind ?? mutationEntityKind(build as Mutation);
    const targetId = target?.id ?? mutationTargetId(build as Mutation);
    const result = await store.transaction(async (tx) => {
      const repo = repoOf(tx, kind);
      const current = await repo.get(targetId);
      if (prepare) await prepare(tx, current);
      const mutation = typeof build === "function" ? await build(current, tx) : build;
      const next = applyMutation(current, mutation, ctx);
      await repo.put(next);
      const { deviceId } = await tx.sync.get();
      const envelope: MutationEnvelope = {
        mutationId: newId(),
        deviceId,
        createdAt: ctx.now,
        mutation,
      };
      if (mutation.type === "notes.updateMarkdown" || mutation.type === "notes.updateContent") {
        const pending = await tx.mutations.pendingFor(targetId);
        const newest = pending[pending.length - 1];
        if (
          newest &&
          newest.mutation.type === mutation.type &&
          !newest.attempted &&
          !inFlight.has(newest.mutationId)
        ) {
          // Coalesce: the user is still typing and nothing has been sent. Keep the older
          // base so the merged write never claims a base the user did not edit from.
          await tx.mutations.replaceMutation(newest.mutationId, {
            ...mutation,
            baseRevision: Math.min(
              mutation.baseRevision,
              (newest.mutation as typeof mutation).baseRevision,
            ),
          });
          return next;
        }
      }
      await tx.mutations.enqueue(envelope);
      return next;
    });
    options.onMutation?.();
    return result as T;
  }

  async function guarded<T extends AnyEntity>(
    kind: EntityKind,
    id: string,
    build: (baseRevision: number) => Mutation,
    baseRevision?: number,
  ): Promise<T> {
    return commit<T>(
      async (current, tx) => {
        if (!current) throw new Error(`${kind} ${id} not found`);
        return build(
          baseRevision === undefined
            ? current.revision
            : await rebaseDraftRevision(tx, kind, id, baseRevision, current.revision),
        );
      },
      { kind, id },
    );
  }

  const tasks: TaskServices = {
    create: (input) => commit({ type: "tasks.create", task: createTask(input, clock()) }),
    createFromQuickAdd: async (text, opts = {}) => {
      const ctx = clock();
      const parsed = parseQuickAdd(text, ctx);
      const projects = opts.projects ?? (await store.projects.list({ status: ["active"] }));
      const wanted = parsed.projectName?.toLowerCase();
      const project = wanted ? projects.find((p) => p.name.toLowerCase() === wanted) : undefined;
      const projectId = project?.id ?? opts.projectId;
      const input: NewTaskInput = { title: parsed.title };
      if (projectId !== undefined) input.projectId = projectId;
      if (opts.sourceNoteId) input.sourceNoteId = opts.sourceNoteId;
      if (opts.triaged) input.triaged = true;
      if (parsed.plannedDate) input.plannedDate = parsed.plannedDate;
      else if (opts.plannedDate && !parsed.scheduledAt && parsed.status !== "someday")
        input.plannedDate = opts.plannedDate;
      if (parsed.dueDate !== undefined) input.dueDate = parsed.dueDate;
      if (parsed.scheduledAt !== undefined) input.scheduledAt = parsed.scheduledAt;
      if (parsed.scheduledDurationMinutes !== undefined)
        input.scheduledDurationMinutes = parsed.scheduledDurationMinutes;
      if (parsed.priority !== undefined) input.priority = parsed.priority;
      if (parsed.evening !== undefined) input.evening = parsed.evening;
      if (parsed.status !== undefined) input.status = parsed.status;
      if (parsed.labels.length > 0) input.tags = parsed.labels;
      return commit({ type: "tasks.create", task: createTask(input, ctx) });
    },
    rename: (id, title) => commit({ type: "tasks.rename", id, title }),
    setTags: (id, tags) => commit({ type: "tasks.setTags", id, tags: [...tags] }),
    setDescription: (id, description, baseRevision) =>
      guarded(
        "task",
        id,
        (revision) => ({
          type: "tasks.setDescription",
          id,
          baseRevision: revision,
          description,
        }),
        baseRevision,
      ),
    setDescriptionContent: (id, content, baseRevision) =>
      guarded(
        "task",
        id,
        (revision) => ({
          type: "tasks.setDescriptionContent",
          id,
          baseRevision: revision,
          document: content?.markdown ? content.document : null,
          markdown: content?.markdown || null,
        }),
        baseRevision,
      ),
    complete: (id) => commit({ type: "tasks.complete", id, completedAt: clock().now }),
    reopen: (id) => commit({ type: "tasks.reopen", id }),
    toggleComplete: async (id) => {
      const current = await store.tasks.get(id);
      if (!current) throw new Error(`task ${id} not found`);
      return current.status === "completed" ? tasks.reopen(id) : tasks.complete(id);
    },
    setStatus: (id, status) => commit({ type: "tasks.setStatus", id, status }),
    setDueDate: (id, dueDate) => commit({ type: "tasks.setDueDate", id, dueDate }),
    plan: (id, plannedDate) => commit({ type: "tasks.plan", id, plannedDate }),
    schedule: (id, scheduledAt, scheduledDurationMinutes = null) =>
      commit({ type: "tasks.schedule", id, scheduledAt, scheduledDurationMinutes }),
    moveToProject: (id, projectId) => commit({ type: "tasks.moveToProject", id, projectId }),
    setPriority: (id, priority) => commit({ type: "tasks.setPriority", id, priority }),
    setEvening: (id, evening) => commit({ type: "tasks.setEvening", id, evening }),
    setHeading: (id, heading) => commit({ type: "tasks.setHeading", id, heading }),
    setEstimate: (id, estimateMinutes) =>
      commit({ type: "tasks.setEstimate", id, estimateMinutes }),
    setParent: (id, parentTaskId) => commit({ type: "tasks.setParent", id, parentTaskId }),
    reorder: (id, sortOrder) => commit({ type: "tasks.reorder", id, sortOrder }),
    delete: (id) => commit({ type: "tasks.delete", id }),
    restore: (id) => commit({ type: "tasks.restore", id }),
  };

  const notes: NoteServices = {
    create: (input = {}) => commit({ type: "notes.create", note: createNote(input, clock()) }),
    extract: async (id, input) => {
      const ctx = clock();
      const child = await store.transaction(async (tx) => {
        const source = await tx.notes.get(id);
        if (!source || source.deletedAt) throw new Error("The source note is no longer available");
        const next = extractNote(source, input.child, ctx);
        const baseRevision =
          input.baseRevision === undefined
            ? source.revision
            : await rebaseDraftRevision(tx, "note", id, input.baseRevision, source.revision);
        const mutations: Mutation[] = [
          { type: "notes.create", note: next },
          {
            type: "notes.updateContent",
            id,
            document: input.source.document,
            markdown: input.source.markdown,
            baseRevision,
          },
        ];
        const { deviceId } = await tx.sync.get();
        await tx.notes.put(next);
        await tx.notes.put(applyMutation(source, mutations[1]!, ctx) as Note);
        for (const mutation of mutations)
          await tx.mutations.enqueue({
            mutationId: newId(),
            deviceId,
            createdAt: ctx.now,
            mutation,
          });
        return next;
      });
      options.onMutation?.();
      return child;
    },
    updateMarkdown: (id, markdown, baseRevision) =>
      guarded(
        "note",
        id,
        (revision) => ({
          type: "notes.updateMarkdown",
          id,
          baseRevision: revision,
          markdown,
        }),
        baseRevision,
      ),
    updateContent: (id, content, baseRevision) =>
      guarded(
        "note",
        id,
        (revision) => ({
          type: "notes.updateContent",
          id,
          baseRevision: revision,
          document: content.document,
          markdown: content.markdown,
        }),
        baseRevision,
      ),
    rename: (id, title, baseRevision) =>
      guarded(
        "note",
        id,
        (revision) => ({
          type: "notes.rename",
          id,
          baseRevision: revision,
          title,
        }),
        baseRevision,
      ),
    move: (id, folderId) => commit({ type: "notes.move", id, folderId }),
    reorder: (id, sortOrder) => commit({ type: "notes.reorder", id, sortOrder }),
    setPinned: (id, pinned) => commit({ type: "notes.setPinned", id, pinned }),
    setTags: (id, tags) => commit({ type: "notes.setTags", id, tags: [...tags] }),
    delete: (id) => commit({ type: "notes.delete", id }),
    restore: (id) => commit({ type: "notes.restore", id }),
    openDaily: async (date, folderId) => {
      const title = dailyNoteTitle(date);
      const existing = await store.notes.findByTitle(title);
      if (existing) return existing;
      const input: NewNoteInput = { title, markdown: "" };
      if (folderId !== undefined) input.folderId = folderId;
      const note = createNote(input, clock());
      // Re-check inside the transaction so two concurrent opens never create two daily notes.
      return commit<Note>({ type: "notes.create", note }, undefined, async (tx) => {
        const raced = await tx.notes.findByTitle(title);
        if (raced) throw new DailyNoteExists(raced);
      }).catch((err: unknown) => {
        if (err instanceof DailyNoteExists) return err.note;
        throw err;
      });
    },
  };

  const folders: FolderServices = {
    create: (input) => commit({ type: "folders.create", folder: createFolder(input, clock()) }),
    rename: (id, name) => commit({ type: "folders.rename", id, name }),
    move: async (id, parentId) => {
      const all = await store.folders.all();
      if (wouldCreateCycle(all, id, parentId)) {
        throw new Error("A folder cannot be moved inside itself");
      }
      return commit({ type: "folders.move", id, parentId });
    },
    reorder: (id, sortOrder) => commit({ type: "folders.reorder", id, sortOrder }),
    delete: (id) => commit({ type: "folders.delete", id }),
    restore: (id) => commit({ type: "folders.restore", id }),
  };

  const projects: ProjectServices = {
    create: (input) => commit({ type: "projects.create", project: createProject(input, clock()) }),
    rename: (id, name) => commit({ type: "projects.rename", id, name }),
    setArea: (id, area) => commit({ type: "projects.setArea", id, area }),
    archive: (id) => commit({ type: "projects.setStatus", id, status: "archived" }),
    unarchive: (id) => commit({ type: "projects.setStatus", id, status: "active" }),
    setNotesFolder: (id, notesFolderId) =>
      commit({ type: "projects.setNotesFolder", id, notesFolderId }),
    reorder: (id, sortOrder) => commit({ type: "projects.reorder", id, sortOrder }),
    delete: (id) => commit({ type: "projects.delete", id }),
    restore: (id) => commit({ type: "projects.restore", id }),
  };

  const reminders: ReminderServices = {
    create: (input) =>
      commit({ type: "reminders.create", reminder: createReminder(input, clock()) }),
    reschedule: (id, triggerAt) => commit({ type: "reminders.reschedule", id, triggerAt }),
    snooze: (id, snoozedUntil) => commit({ type: "reminders.snooze", id, snoozedUntil }),
    dismiss: (id) => commit({ type: "reminders.dismiss", id }),
    markDelivered: (id) => commit({ type: "reminders.markDelivered", id }),
    delete: (id) => commit({ type: "reminders.delete", id }),
  };

  const areas: AreaServices = {
    create: (input) => commit({ type: "areas.create", area: createArea(input, clock()) }),
  };
  return { tasks, notes, folders, projects, areas, reminders, inFlight };
}

class DailyNoteExists extends Error {
  constructor(readonly note: Note) {
    super("Daily note already exists");
  }
}
